import type { DocumentMapping } from "@/configuration/document-mappings/types";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { Classification } from "@/src/models/constants/classifications";

// Confirmed live (2026-10-04) against GET /users — real ACS staff, "Last, First" display form.
const ACS_CRYSTAL_MEANS = "Means, Crystal"; // userId 671
const ACS_REBECCA_WOOD = "Wood, Rebecca"; // userId 218
const ACS_ANDREA_TUCKER = "Tucker, Andrea"; // userId 135
const ACS_CHRISTINA_GRAY = "Gray, Christina"; // userId 154
const ACS_AMBER_WISE = "Wise, Amber"; // userId 131
const ACS_COURTNEY_EDWARDS = "Edwards, Courtney"; // userId 242
const ACS_ANGELA_VARELA = "Varela, Angela"; // userId 137
// Confirmed live (2026-10-04) against GET /master/tasks/workgroups — workgroupId 31.
const ACS_PHARMACY_WORKGROUP = "Workgroup: Pharmacy";

export const ACS_MAPPINGS: Partial<Record<Classification, DocumentMapping>> = {
  [Classification.REFERRAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    // Fallback only; replaced by buildAcsReferralInstructions.
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Referral received {{ dateReceived }}.",
      "Task Note: No qualifying office visit within 3 years — handle as a new patient for scheduling.",
    ],
    assignedTo: ACS_CRYSTAL_MEANS,
    displayName: "Referral",
    documentLabel: "{{ dateReceived }} Referral - {{ patientInitials }}",
    folder: "Correspondence Incoming",
    folderMapping: "a34aaccd-5b20-41e3-bd3c-777e716b26da",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Inbound Referral WF",
    priority: "Normal",
    classification: Classification.REFERRAL,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Medication prior authorization {{ authorizationStatus }} received {{ dateReceived }}. Please review in Document Management — denials are wanted, do not discard.",
    ],
    assignedTo: ACS_PHARMACY_WORKGROUP,
    displayName: "Prior Authorization Medication Request",
    documentLabel: "{{ dateReceived }} Med PA Request - {{ patientInitials }}",
    folder: "RX PA/PAP",
    folderMapping: "f6cadefb-cd05-46e0-81a6-1b37a080570b",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
    group: ConfigSubdomain.ACS,
  },
  [Classification.LAB_RESULTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Lab result collected {{ collectionDate }}. Please review in Document Management.",
    ],
    // PT/INR branch (folder, task note, freshness override) built in buildAcsLabResultsInstructions.
    // Folder varies by typeOfLab (getAcsDocumentTypeId); this is the default.
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Lab Results",
    documentLabel: "{{ collectionDate }} Labs - {{ patientInitials }}",
    folder: "Labs",
    folderMapping: "3deaecca-411a-4326-96e1-8f5dd3686594",
    // "Date of Event" would resolve via INPUT_DATE_TO_FIELD to reviewedContent.dateOfEvent, but
    // this classification's real field is collectionDate (labResults.collectionDate in
    // blocks.yaml) — same bug the bot caught on PROGRESS_NOTE.
    inputDate: "Date of Collection",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.LAB_RESULTS,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_CT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: CT results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    // Folder varies by isOverread (getAcsDocumentTypeId).
    assignedTo: "Owning Nurse Practitioner",
    displayName: "CT",
    documentLabel: "{{ dateOfEvent }} CT - {{ patientInitials }}",
    folder: "CT Scans",
    folderMapping: "c4af66cf-ab45-46a1-ac9d-5abfeba64bee",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_CT,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_ECHO]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Echo results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    // Folder varies by studyType (getAcsDocumentTypeId). TODO: "both" should also drop the cover page.
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Echo",
    documentLabel: "{{ dateOfEvent }} Echo - {{ patientInitials }}",
    folder: "ECHO",
    folderMapping: "a6f4f053-6a47-469b-9342-fce572a9c290",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_ECHO,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_MRI]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: MRI results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    // Folder varies by bodyPart (getAcsDocumentTypeId); this is the default.
    assignedTo: "Owning Nurse Practitioner",
    displayName: "MRI",
    documentLabel: "{{ dateOfEvent }} MRI - {{ patientInitials }}",
    folder: "MRI",
    folderMapping: "a3a06be6-e20a-4b91-9871-2ced8c634ee5",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_MRI,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_X_RAY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Chest X-Ray results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    // Folder varies by bodyPart (getAcsDocumentTypeId); this is the default.
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Chest X-Ray",
    documentLabel: "{{ dateOfEvent }} Chest X-Ray - {{ patientInitials }}",
    folder: "Chest X-Ray",
    folderMapping: "b3fcac60-c086-451e-a31f-bed24ac1c975",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_X_RAY,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_EKG]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: EKG results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "EKG",
    documentLabel: "{{ dateOfEvent }} EKG - {{ patientInitials }}",
    folder: "EKG",
    folderMapping: "cee99034-8204-43a8-93b1-663385b2e2fb",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_EKG,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_PET]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: PET results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    // Real catalog splits PET into "PET Images"/"PET Paperwork"/"PET Results" (plus PET/CT and
    // stress-PET variants) — no single generic "PET" folder, and no field to distinguish those
    // sub-variants. "PET Results" is the closest general match for a PET results document. Folder
    // does vary by bodyPart (getAcsDocumentTypeId): cardiac -> "Cardiac PET Test".
    assignedTo: "Owning Nurse Practitioner",
    displayName: "PET",
    documentLabel: "{{ dateOfEvent }} PET - {{ patientInitials }}",
    folder: "PET Results",
    folderMapping: "1908fcae-54c0-429b-9170-408a6761815a",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_PET,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PHARMACY_RELATED_REFILL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Pharmacy refill request received {{ dateReceived }}. Please review in Document Management.",
    ],
    // "Prescriptions" (not yet signed) fits a refill request better than "Signed Prescritions".
    assignedTo: ACS_PHARMACY_WORKGROUP,
    displayName: "Pharmacy Refill",
    documentLabel: "{{ dateReceived }} Rx Refill - {{ patientInitials }}",
    folder: "Prescriptions",
    folderMapping: "474b485c-0a03-4dc8-883a-cc4522e58547",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PHARMACY_RELATED_REFILL,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PHARMACY_RELATED_ALERT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Pharmacy Alert",
    documentLabel: "{{ dateReceived }} Pharmacy Alert",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "No Routing WF",
    priority: "Normal",
    classification: Classification.PHARMACY_RELATED_ALERT,
    group: ConfigSubdomain.ACS,
  },
  [Classification.ORDER_FROM_PHYSICIAN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Physician order received {{ dateReceived }}. Please review in Document Management.",
    ],
    assignedTo: ACS_CRYSTAL_MEANS,
    displayName: "Order From Physician",
    documentLabel: "{{ dateReceived }} Order - {{ patientInitials }}",
    // No generic "Orders" folder in the real catalog — only sub-typed ones (Outside Orders, Rehab
    // Orders, DME Orders, Oxygen Orders, Home Health Orders). "Outside Orders" fits inbound imaging
    // orders from another physician best. Open item #1 in the reviewed sheet also flags this
    // classification as unconfirmed in scope — folder choice should still be revisited with ACS.
    folder: "Outside Orders",
    folderMapping: "3ec8c55c-a958-4aef-a3e2-9281f58f70d9",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    // Own type, not "Standard" — keeps patient-creation; see workflow-steps.ts.
    workflowType: "Order From Physician WF",
    priority: "Normal",
    classification: Classification.ORDER_FROM_PHYSICIAN,
    group: ConfigSubdomain.ACS,
  },
  [Classification.ADMIN_DISABILITY_FORMS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Disability form received {{ dateReceived }}. Complete the form, route for provider signature, then notify the patient.",
    ],
    assignedTo: ACS_ANDREA_TUCKER,
    displayName: "Disability Form",
    documentLabel: "{{ dateReceived }} Disability Form - {{ patientInitials }}",
    folder: "Disability",
    folderMapping: "876afcb0-cd3e-4b20-baf3-f508af1c7d31",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.ADMIN_DISABILITY_FORMS,
    group: ConfigSubdomain.ACS,
  },
  [Classification.JUNK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Junk",
    documentLabel: "{{ dateReceived }} Junk - {{ patientInitials }}",
    // Includes St. E notifications of studies ACS performed and already holds, which carry no
    // results, per the reviewed sheet.
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    priority: "Normal",
    classification: Classification.JUNK,
    group: ConfigSubdomain.ACS,
  },
  [Classification.BLANK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Blank",
    documentLabel: "{{ dateReceived }} Blank",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    priority: "Normal",
    classification: Classification.BLANK,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PROGRESS_NOTE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Progress note from {{ dateOfProgressNote }}. Please review in Document Management.",
    ],
    // Nulled by getAcsDocumentManagementInstructions when dateOfProgressNote is older than 3 months.
    assignedTo: ACS_CHRISTINA_GRAY,
    displayName: "Progress Note",
    documentLabel: "{{ dateOfProgressNote }} Progress Note - {{ patientInitials }}",
    // Folder varies by specialty (getAcsDocumentTypeId), using the generic `Specialty` enum rather
    // than the Pinnacle-coupled `specialtyFolder` one. This static folder is unused at runtime
    // (getAcsDocumentTypeId never falls through to it) but kept for template/display purposes.
    // "Delete the cover page" is also deferred — no existing mapping expresses page-level deletion;
    // it needs a workflow step, not a DocumentMapping field.
    folder: "Progress Notes",
    folderMapping: "82987404-c5bf-4aea-9d18-7014e4b8e993",
    inputDate: "Date of Progress Note",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PROGRESS_NOTE,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PROGRESS_NOTE_HOSPITAL_RECORDS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    // No task, regardless of date — these are ACS's own providers' notes, per the reviewed sheet.
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Cardiology Hospital Consult",
    // hospitalAbbrev + provider initials (e.g. "STE MJF") is left as a follow-up — needs a
    // dedicated provider-initials helper, not a plain template var.
    documentLabel: "{{ dateReceived }} Cardiology Hospital Consult - {{ patientInitials }}",
    // Folder varies by receivingProvider/isConsult (getAcsDocumentTypeId); this is the fallback.
    folder: "Cardiology Hospital Consult",
    folderMapping: "1342ec16-2582-4169-b819-f3629e2ab73f",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PROGRESS_NOTE_HOSPITAL_RECORDS,
    group: ConfigSubdomain.ACS,
  },
  [Classification.CLEARANCE_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    // Fully replaced by getAcsDocumentManagementInstructions, which branches on the
    // lastOfficeVisitDate/patientLocation chart lookup (nextgen-last-qualifying-encounter-fetch.ts)
    // plus the extracted schedulingPriority/dateOfEvent fields. This is just a fallback for when
    // the chart lookup finds nothing (no qualifying encounter — same as "older than 3 years").
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Surgical clearance request. Handle as a new patient for scheduling.",
    ],
    assignedTo: ACS_CRYSTAL_MEANS,
    displayName: "Surgical Clearance",
    documentLabel: "{{ dateReceived }} Surgical Clearance - {{ patientInitials }}",
    folder: "Surgical Clearance",
    folderMapping: "3e29be43-a49e-41e7-bdfa-264631e7195d",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Clearance Request WF",
    priority: "Normal",
    classification: Classification.CLEARANCE_REQUEST,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PROCEDURE_DEVICE_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Device/implant transfer request received {{ dateReceived }}. Complete the MRI-compatibility form before filing.",
    ],
    assignedTo: ACS_AMBER_WISE,
    displayName: "Device/Implant Transfer Request",
    documentLabel: "{{ dateReceived }} Device Transfer Request - {{ patientInitials }}",
    // workflowType flipped from "Task" (no EHR action) to "Standard" so this now uploads.
    folder: "MRI Clearance",
    folderMapping: "5db00315-5642-41df-98f3-343beba88ba7",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PROCEDURE_DEVICE_REQUEST,
    group: ConfigSubdomain.ACS,
  },
  [Classification.MEDICAL_RECORD_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    // Assignee is fully replaced by getAcsDocumentManagementInstructions, which reroutes to Taylor
    // Wensley (Healthmark) when isLegal is true or documentType is "whole chart". Folder
    // also varies by isLegal (getAcsDocumentTypeId). workflowType flipped from "Task" to
    // "Standard" so this now uploads.
    // Multi-patient requests don't reach this mapping — per the reviewed sheet, they're emailed to
    // Crystal Means instead of filed/tasked. See getAcsEffectiveWorkflowType (workflow-steps.ts),
    // which overrides the workflow type to SPECIAL_CASES for that subset.
    additionalWorkflow: ["Task Type: To-Do", "Task Details: Medical record request received {{ dateReceived }}."],
    assignedTo: ACS_COURTNEY_EDWARDS,
    displayName: "Medical Record Request",
    documentLabel: "{{ dateReceived }} Medical Record Request - {{ patientInitials }}",
    folder: "Medical Records Request",
    folderMapping: "f9bfe3c5-0d4a-43de-9352-8c11d16bbee5",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.MEDICAL_RECORD_REQUEST,
    group: ConfigSubdomain.ACS,
  },
  [Classification.MEDICAL_RECORD_REQUEST_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    // Assignee is replaced by getAcsDocumentManagementInstructions with the receiving provider (the
    // requester); the task is dropped when none was extracted.
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Response to medical record request received {{ dateReceived }}. Please review.",
    ],
    assignedTo: null,
    displayName: "Medical Record Request Response",
    documentLabel: "{{ dateReceived }} Medical Record Request Response - {{ patientInitials }}",
    folder: "Medical Records Request",
    folderMapping: "f9bfe3c5-0d4a-43de-9352-8c11d16bbee5",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.MEDICAL_RECORD_REQUEST_RESPONSE,
    group: ConfigSubdomain.ACS,
  },
  [Classification.ORDER_CLARIFICATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    // Fully replaced by getAcsDocumentManagementInstructions, which reroutes to Misty Fontenot for
    // cardiac rehab/sleep study procedures or Jessica Wells for CT/CTA/cardiac MRI procedures or a
    // Dr. Hong receiving provider. This is the default (no signal matches), per the reviewed sheet's
    // base assignee. Per the reviewed sheet's own open item, "outgoing referral" routing has no
    // reliable field and is intentionally not built — see PR description.
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Order clarification received from {{ senderName }}. Keep only the actionable page — the rest is already in the chart.",
    ],
    assignedTo: ACS_ANGELA_VARELA,
    displayName: "Order Clarification",
    documentLabel: "Correspondence Incoming",
    folder: "Correspondence Incoming",
    folderMapping: "a34aaccd-5b20-41e3-bd3c-777e716b26da",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.ORDER_CLARIFICATION,
    group: ConfigSubdomain.ACS,
  },
  [Classification.CARE_PLAN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    // Fully replaced by getAcsDocumentManagementInstructions, which branches on serviceLine/
    // isSigned/documentType. Folder also varies by serviceLine — see getAcsDocumentTypeId
    // (configuration.ts). Home health + isSigned=false additionally reroutes the whole workflow to
    // EXTERNAL_ROUTING_WF (no upload, email only) — see getAcsEffectiveWorkflowType in workflow-steps.ts.
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Care plan received {{ dateReceived }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Care Plan",
    documentLabel: "{{ dateReceived }} Care Plan - {{ patientInitials }}",
    folder: "Cardiac Rehab Summary",
    folderMapping: "80390504-4ce7-4132-b2a3-d11c074cb292",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.CARE_PLAN,
    group: ConfigSubdomain.ACS,
  },
  [Classification.REFERRAL_STATUS_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    // Multi-patient status requests don't reach this mapping — per the reviewed sheet, they're
    // emailed to Crystal Means instead of filed/tasked. See getAcsEffectiveWorkflowType
    // (workflow-steps.ts), which overrides the workflow type to SPECIAL_CASES for that subset.
    additionalWorkflow: ["Task Type: To-Do", "Task Details: Referral status request received {{ dateReceived }}."],
    assignedTo: ACS_CRYSTAL_MEANS,
    displayName: "Referral Status Request",
    documentLabel: "{{ dateReceived }} Referral Status Request - {{ patientInitials }}",
    folder: "Correspondence Incoming",
    folderMapping: "a34aaccd-5b20-41e3-bd3c-777e716b26da",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.REFERRAL_STATUS_REQUEST,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PRIOR_AUTHORIZATION_REFERRAL_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    // Default assignee is Rebecca Wood. Replaced by getAcsDocumentManagementInstructions for
    // VA/TriWest (Lesly Allen or Crystal Means) and forced back to Rebecca Wood on denial.
    // Folder varies by companyName/authorizationStatus (getAcsDocumentTypeId); this is the fallback.
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Prior authorization referral response received {{ dateReceived }}. Drop the payer logo/cover page — retain only the page carrying the authorization number and CPT code.",
    ],
    assignedTo: ACS_REBECCA_WOOD,
    displayName: "Prior Authorization Referral Response",
    documentLabel: "{{ dateReceived }} Prior Auth Referral Response - {{ patientInitials }}",
    folder: "Pre Authorization Referral Notes",
    folderMapping: "a8ccfb65-c071-4b77-aff0-d09d1c28f15f",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Prior Authorization Referral Response WF",
    priority: "Normal",
    classification: Classification.PRIOR_AUTHORIZATION_REFERRAL_RESPONSE,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    // Assignee is static (Rebecca Wood). Folder varies by authorizationStatus (getAcsDocumentTypeId);
    // this is the non-denial default.
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Prior authorization procedure response received {{ dateReceived }}. Enter on the Authorization tab.",
    ],
    assignedTo: ACS_REBECCA_WOOD,
    displayName: "Prior Authorization Procedure Response",
    documentLabel: "{{ dateReceived }} Prior Auth Procedure Response - {{ patientInitials }}",
    folder: "Precerts",
    folderMapping: "af15e588-675c-4b4d-a826-3a7d00d8740f",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
    group: ConfigSubdomain.ACS,
  },
  [Classification.COVER_PAGE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Cover Page",
    documentLabel: "{{ dateReceived }} Cover Page - Discarded",
    // The reviewed sheet's exception ("retain the page on a CLEARANCE_REQUEST, it carries the
    // sender's callback number") can't be built — cover-page.ts has no fields beyond patient, so
    // there's no way to detect which bundle a standalone cover page belongs to. Drops unconditionally.
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    priority: "Normal",
    classification: Classification.COVER_PAGE,
    group: ConfigSubdomain.ACS,
  },
  [Classification.INCOMPLETE_CANNOT_LINK_TO_PATIENT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Cannot Link to Patient",
    documentLabel: "{{ dateReceived }} Cannot Link to Patient",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "No Routing WF",
    priority: "Normal",
    classification: Classification.INCOMPLETE_CANNOT_LINK_TO_PATIENT,
    group: ConfigSubdomain.ACS,
  },
  [Classification.INCOMPLETE_ILLEGIBLE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Document boundaries could not be resolved (e.g. run-together fax pages). Hold in the review queue — do not upload.",
    ],
    assignedTo: null,
    displayName: "Illegible",
    documentLabel: "{{ dateReceived }} Illegible",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Task",
    priority: "Normal",
    classification: Classification.INCOMPLETE_ILLEGIBLE,
    group: ConfigSubdomain.ACS,
  },
};
